import { LEISTUNGSPHASEN, lphGruppeFuer } from '@asas/domain/lph-gruppen';
import type { LphGroup, Permission, WorkPackageLibraryEntry } from '@asas/domain';

import type { FeldBefund } from '../daten/fehler';

/**
 * Reine Logik der Ansicht „LPH-Gruppierung“ (AC-305): Entwurf bearbeiten,
 * Vorschau, Feldmeldungen. Ohne Aufrufe und ohne React, damit sie ohne
 * Browser prüfbar ist.
 *
 * Geprüft wird am Server (`pruefeLphGruppen`, AC-101) — hier steht keine
 * zweite Kopie der Regeln. Die Ansicht zeigt dessen Befunde am Feld.
 */

/**
 * Das Recht hinter `tenant.updateSettings`. Die Ansicht erscheint nur damit
 * (AC-305 E12); `tenant.settings` selbst darf jeder lesen.
 */
export const LPH_GRUPPIERUNG_PFLEGERECHT: Permission = 'tenant:configure';

/** Das Recht hinter `workPackageLibrary.list`; ohne es entfällt die Vorschau (AK 4, E7). */
export const LPH_VORSCHAURECHT: Permission = 'work_package_library:read';

export function darfLphGruppierungPflegen(rechte: ReadonlySet<Permission>): boolean {
  return rechte.has(LPH_GRUPPIERUNG_PFLEGERECHT);
}

export function darfLphVorschau(rechte: ReadonlySet<Permission>): boolean {
  return rechte.has(LPH_VORSCHAURECHT);
}

/** Eine Gruppe, wie sie im Formular steht: Bezeichnung ungetrimmt, Phasen aufsteigend. */
export interface GruppenEntwurf {
  readonly label: string;
  readonly phasen: readonly number[];
}

export function entwurfAus(gruppen: readonly LphGroup[]): GruppenEntwurf[] {
  return gruppen.map((gruppe) => ({
    label: gruppe.label,
    phasen: [...gruppe.phases].sort((a, b) => a - b),
  }));
}

/** Die Eingabe für `tenant.updateSettings`. Getrimmt wird am Server. */
export function alsEingabe(entwurf: readonly GruppenEntwurf[]): LphGroup[] {
  return entwurf.map((gruppe) => ({ label: gruppe.label, phases: [...gruppe.phasen] }));
}

/** Ändert der Entwurf etwas am gespeicherten Stand? Reihenfolge zählt (Anzeigereihenfolge). */
export function istGeaendert(
  entwurf: readonly GruppenEntwurf[],
  gespeichert: readonly LphGroup[],
): boolean {
  const basis = entwurfAus(gespeichert);

  return (
    entwurf.length !== basis.length ||
    entwurf.some((gruppe, index) => {
      const andere = basis[index];

      return (
        andere === undefined ||
        gruppe.label !== andere.label ||
        gruppe.phasen.length !== andere.phasen.length ||
        gruppe.phasen.some((phase, i) => phase !== andere.phasen[i])
      );
    })
  );
}

export interface NeuerStand {
  readonly entwurf: GruppenEntwurf[];
  /**
   * Die inzwischen gespeicherte Gruppierung, wenn der Entwurf eigene Eingaben
   * trägt UND sich der Stand geändert hat — ein Hinweis, kein Fehler. Wer dann
   * speichert, ersetzt sie bewusst (AC-409: der Mensch entscheidet).
   */
  readonly hinweis: readonly LphGroup[] | null;
}

/**
 * Ein neuer Stand vom Server (Neu laden nach einem Konflikt): ohne eigene
 * Eingaben gilt er, mit eigenen bleiben diese (apps/web/CLAUDE.md, „Ein
 * Formular merkt sich den Stand“). Die Gruppierung ist ein Ganzes; ein
 * Zusammenführen je Gruppe gibt es nicht (ADR-14).
 */
export function aufNeuenStand(
  entwurf: readonly GruppenEntwurf[],
  basis: readonly LphGroup[],
  neu: readonly LphGroup[],
): NeuerStand {
  if (!istGeaendert(entwurf, basis)) {
    return { entwurf: entwurfAus(neu), hinweis: null };
  }

  return {
    entwurf: [...entwurf],
    hinweis: istGeaendert(entwurfAus(basis), neu) ? neu : null,
  };
}

export function phaseUmschalten(
  entwurf: readonly GruppenEntwurf[],
  index: number,
  phase: number,
): GruppenEntwurf[] {
  return entwurf.map((gruppe, i) => {
    if (i !== index) return gruppe;

    const phasen = gruppe.phasen.includes(phase)
      ? gruppe.phasen.filter((p) => p !== phase)
      : [...gruppe.phasen, phase].sort((a, b) => a - b);

    return { ...gruppe, phasen };
  });
}

export function gruppeUmbenennen(
  entwurf: readonly GruppenEntwurf[],
  index: number,
  label: string,
): GruppenEntwurf[] {
  return entwurf.map((gruppe, i) => (i === index ? { ...gruppe, label } : gruppe));
}

/** Höchstens neun Gruppen (`LPH_GROUPS_MAX`): jede Phase in höchstens einer, jede Gruppe mit einer. */
export const LPH_GRUPPEN_HOECHSTENS = LEISTUNGSPHASEN.length;

export function gruppeHinzufuegen(entwurf: readonly GruppenEntwurf[]): GruppenEntwurf[] {
  return entwurf.length >= LPH_GRUPPEN_HOECHSTENS
    ? [...entwurf]
    : [...entwurf, { label: '', phasen: [] }];
}

/** Mindestens eine Gruppe bleibt (AK 1); die letzte lässt sich nicht entfernen. */
export function gruppeEntfernen(
  entwurf: readonly GruppenEntwurf[],
  index: number,
): GruppenEntwurf[] {
  return entwurf.length <= 1 ? [...entwurf] : entwurf.filter((_, i) => i !== index);
}

/** „LPH 1, 2“ — für „ohne Gruppe“ und die Vorschau. */
export function phasenAnzeige(phasen: readonly number[]): string {
  return phasen.length === 0 ? 'keine' : `LPH ${phasen.join(', ')}`;
}

export interface Vorschaueintrag {
  readonly eintrag: WorkPackageLibraryEntry;
  /** Passt schon zur gespeicherten Gruppierung nicht — keine Folge dieser Änderung. */
  readonly schonHeute: boolean;
}

/**
 * Die aktiven Stammarbeitspakete, die nach dem Speichern des Entwurfs keiner
 * Gruppe entsprechen (AK 4). Dieselbe Regel wie beim Schreiben in der
 * Bibliothek (`lphGruppeFuer`, AC-301 H2). Archivierte Einträge sind für neue
 * Instanzen ohnehin nicht wählbar und zählen nicht.
 */
export function vorschau(
  eintraege: readonly WorkPackageLibraryEntry[],
  entwurf: readonly GruppenEntwurf[],
  gespeichert: readonly LphGroup[],
): Vorschaueintrag[] {
  const neu = alsEingabe(entwurf);

  return eintraege
    .filter((eintrag) => eintrag.archivedAt === null)
    .filter((eintrag) => lphGruppeFuer(eintrag.leistungsphasen, neu) === null)
    .map((eintrag) => ({
      eintrag,
      schonHeute: lphGruppeFuer(eintrag.leistungsphasen, gespeichert) === null,
    }));
}

export interface GruppenMeldungen {
  /** Je Gruppenindex die erste Meldung an Bezeichnung bzw. Phasen. */
  readonly jeGruppe: ReadonlyMap<number, { readonly label?: string; readonly phasen?: string }>;
  /** Befunde ohne Gruppenbezug (etwa „mindestens eine Gruppe“); gehen nicht verloren. */
  readonly allgemein: readonly string[];
}

const GRUPPENPFAD = /^lphGroups\.(\d+)\.(label|phases)(?:\.\d+)?$/;

/** Ordnet die Feldbefunde der API (`lphGroups.<i>.label`, `lphGroups.<i>.phases.<j>`) den Gruppen zu. */
export function gruppenMeldungen(felder: readonly FeldBefund[]): GruppenMeldungen {
  const jeGruppe = new Map<number, { label?: string; phasen?: string }>();
  const allgemein: string[] = [];

  for (const feld of felder) {
    const treffer = GRUPPENPFAD.exec(feld.path);

    if (treffer === null) {
      allgemein.push(feld.message);
      continue;
    }

    const index = Number(treffer[1]);
    const vorher = jeGruppe.get(index) ?? {};

    if (treffer[2] === 'label') {
      jeGruppe.set(index, { ...vorher, label: vorher.label ?? feld.message });
    } else {
      jeGruppe.set(index, { ...vorher, phasen: vorher.phasen ?? feld.message });
    }
  }

  return { jeGruppe, allgemein };
}

/**
 * Was vor einer Neuanmeldung gemerkt wird: der Entwurf UND der Stand, von dem
 * aus getippt wurde (apps/web/CLAUDE.md, „Ein Formular merkt sich den Stand“).
 * Ohne `basis` landete ein geborgener Entwurf ungeprüft über einem inzwischen
 * fremd geänderten Stand und drehte ihn beim Speichern still zurück.
 */
export interface GemerkterEntwurf {
  readonly entwurf: GruppenEntwurf[];
  readonly basis: LphGroup[];
}

function pruefeGruppenListe(
  roh: unknown,
  feld: 'phasen' | 'phases',
): { label: string; phasen: number[] }[] | null {
  if (!Array.isArray(roh) || roh.length === 0 || roh.length > LPH_GRUPPEN_HOECHSTENS) return null;

  const erlaubt = new Set<number>(LEISTUNGSPHASEN);
  const ergebnis: { label: string; phasen: number[] }[] = [];

  for (const eintrag of roh as unknown[]) {
    if (typeof eintrag !== 'object' || eintrag === null) return null;

    const label: unknown = (eintrag as Record<string, unknown>).label;
    const phasen: unknown = (eintrag as Record<string, unknown>)[feld];

    if (typeof label !== 'string' || !Array.isArray(phasen)) return null;
    if (!phasen.every((p): p is number => typeof p === 'number' && erlaubt.has(p))) return null;

    ergebnis.push({ label, phasen: [...phasen].sort((a, b) => a - b) });
  }

  return ergebnis;
}

/**
 * Prüfer für einen Entwurf aus dem `sessionStorage` (Pflicht beim Auslesen,
 * `daten/entwurf.ts`). Grob: Form und Phasen 1–9; den Rest prüft der Server.
 */
export function pruefeGemerktenEntwurf(roh: unknown): GemerkterEntwurf | null {
  if (typeof roh !== 'object' || roh === null) return null;

  const entwurf = pruefeGruppenListe((roh as Record<string, unknown>).entwurf, 'phasen');
  const basis = pruefeGruppenListe((roh as Record<string, unknown>).basis, 'phases');

  if (entwurf === null || basis === null) return null;

  return { entwurf, basis: basis.map((g) => ({ label: g.label, phases: g.phasen })) };
}

export interface Geborgen extends NeuerStand {
  /** Für die Statuszeile. */
  readonly meldung: string;
}

/**
 * Ein gemerkter Entwurf trifft nach der Neuanmeldung auf den aktuellen Stand:
 * dieselbe Regel wie nach einem Konflikt (`aufNeuenStand`). Hat jemand anderes
 * inzwischen gespeichert, bleibt der eigene Entwurf, und der Hinweis nennt den
 * fremden Stand. Abgeschickt wird nie automatisch.
 */
export function bergen(gemerkt: GemerkterEntwurf, neu: readonly LphGroup[]): Geborgen {
  const stand = aufNeuenStand(gemerkt.entwurf, gemerkt.basis, neu);

  if (!istGeaendert(stand.entwurf, neu)) {
    return {
      ...stand,
      meldung: 'Ihre Eingaben von vor der Anmeldung entsprechen dem gespeicherten Stand.',
    };
  }

  return {
    ...stand,
    meldung:
      'Ihre Eingaben von vor der Anmeldung wurden wiederhergestellt. Bitte prüfen und speichern.' +
      (stand.hinweis === null
        ? ''
        : ' Inzwischen hat jemand anderes gespeichert; der gespeicherte Stand steht unten.'),
  };
}

/**
 * Was vor einer Neuanmeldung mit dem gemerkten Entwurf geschieht:
 *
 *   - Formular noch nicht geladen (`basis` oder `entwurf` fehlt): BEHALTEN. Ein
 *     Entwurf aus einer früheren Sitzung darf nicht verloren gehen, nur weil
 *     das erste Laden mit 401 scheitert (N1 aus dem KI-Agent-Review).
 *   - Geladen, aber ohne eigene Eingaben: VERGESSEN — sonst erschiene ein
 *     älterer Entwurf nach der Anmeldung als „wiederhergestellt“ (H6).
 *   - Eigene Eingaben: MERKEN, mit dem Stand, von dem aus getippt wurde (B1).
 */
export type Sicherung =
  | { readonly art: 'behalten' }
  | { readonly art: 'vergessen' }
  | { readonly art: 'merken'; readonly gemerkt: GemerkterEntwurf };

export function sicherung(
  basis: readonly LphGroup[] | null,
  entwurf: readonly GruppenEntwurf[] | null,
): Sicherung {
  if (basis === null || entwurf === null) return { art: 'behalten' };
  if (!istGeaendert(entwurf, basis)) return { art: 'vergessen' };

  return { art: 'merken', gemerkt: { entwurf: [...entwurf], basis: [...basis] } };
}
